/**
 * Prioris — Materials Approval family summary view.
 *
 * Rendered when the user clicks a material family (Concrete / Cement /
 * Aggregates / SCMs / Water / Admixtures / Rebar / Structural Steel / Asphalt /
 * Earthworks / Waterproofing / Grouting) in the sidebar.
 *
 * Shows a dense, spec-focused summary of approved & in-progress submittals
 * for that family, with columns tailored to what engineers actually need
 * to see at a glance (e.g. for concrete: cement type, cement kg/m³, SCM %,
 * w/c ratio, declared fc28).
 *
 * Across assets by default; narrowed to the selected asset when one is active.
 */
import { useEffect, useMemo, useState } from 'react';
import { fetchSln } from '../nexus/nexusHelpers';
import {
  ArrowLeft, Loader2, FileText, ExternalLink, Layers, AlertCircle, Plus,
} from 'lucide-react';

const STATUS_COLOURS = {
  'Draft':           { bg: 'rgba(148,163,184,0.15)',  fg: '#94a3b8' },
  'Under Review':    { bg: 'rgba(59,130,246,0.18)',   fg: '#60a5fa' },
  'Action Required': { bg: 'rgba(245,158,11,0.18)',   fg: '#fbbf24' },
  'Approved':        { bg: 'rgba(16,185,129,0.18)',   fg: '#34d399' },
  'Rejected':        { bg: 'rgba(239,68,68,0.18)',    fg: '#f87171' },
  'Superseded':      { bg: 'rgba(168,85,247,0.15)',   fg: '#c084fc' },
  'Cancelled':       { bg: 'rgba(100,116,139,0.15)',  fg: '#94a3b8' },
};

/**
 * Column packs per family. Each column has:
 *   { label, get(row) => display value }
 * Shared columns live in BASE.
 */
const BASE_COLS = [
  { label: 'Ref',       key: 'internal_ref',       get: r => r.internal_ref,       strong: true, clickable: true },
  { label: 'Clause',    key: 'spec_rubrica_code',  get: r => r.spec_rubrica_code || '—' },
  { label: 'Product',   key: 'product_description', get: r => r.product_description || '—' },
  { label: 'Producer',  key: 'producer',           get: r => r.producer || '—' },
  { label: 'Location',  key: 'sln_location',       get: r => {
      const z = r.sln_zone_name; const e = r.sln_element_name;
      if (!z && !e) return '—';
      return e ? `${z} → ${e}` : z;
    } },
  { label: 'Standard',  key: 'applicable_standard', get: r => r.applicable_standard || '—' },
];

const STATUS_COL = {
  label: 'Status', key: 'status',
  get: r => {
    const c = STATUS_COLOURS[r.status] || STATUS_COLOURS.Draft;
    return (
      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: c.bg, color: c.fg }}>{r.status}</span>
    );
  },
};

const num = (v, digits = 0) => (v == null || v === '' ? '—' : (+v).toFixed(digits));

/** Family columns are now built dynamically from admin-defined custom_fields.
 *  For concrete_mix, the computed mix-design columns are still added
 *  (cement kg/m³, SCM %, w/cm, fc28) — see the useMemo in the component. */


export default function MaFamilySummary({ t, family, projectId, onBack, specRubricaCode, specTableKey, specRubricaLabel }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerRow, setDrawerRow] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'approved' | 'in_progress'
  const [customFields, setCustomFields] = useState([]);
  const [specTable, setSpecTable] = useState(null);        // full spec table when specTableKey set

  useEffect(() => {
    if (!specTableKey) { setSpecTable(null); return; }
    fetchSln(`/api/prioris/spec-catalog/tables/${specTableKey}`).then(setSpecTable).catch(() => setSpecTable(null));
  }, [specTableKey]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const url = projectId
      ? `/api/prioris/ma/submittals?project_id=${projectId}`
      : '/api/prioris/ma/submittals';
    Promise.all([
      fetchSln(url),
      fetchSln('/api/prioris/ma/families'),
    ]).then(([d, fd]) => {
      if (!live) return;
      const allSubmittals = d.submittals || [];
      const filtered = specRubricaCode
        ? allSubmittals.filter(s => s.spec_rubrica_code === specRubricaCode)
        : allSubmittals.filter(s => s.material_family === family.family);
      setRows(filtered);
      const famKey = family.family || family.key;
      const fam = (fd.families || []).find(f => f.key === famKey);
      setCustomFields(fam?.custom_fields || []);
    }).catch(() => { if (live) { setRows([]); setCustomFields([]); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [family.key, family.family, projectId, specRubricaCode]);

  // Build columns: base + dynamic per-rubrica columns (only properties that are
  // "active" for this rubrica — i.e. not NA). When no rubrica is selected we
  // fall back to the previous family-based columns + admin custom_fields.
  const cols = useMemo(() => {
    const out = [...BASE_COLS];
    const famKey = family.family || family.key;

    // ── Spec-driven: only show cols for properties active on this rubrica ──
    if (specRubricaCode && specTable) {
      const activeProps = specTable.properties.filter(p => {
        const cell = p.values?.[specRubricaCode];
        if (!cell) return false;
        if (cell.na) return false;
        return true;
      });
      activeProps.forEach(p => {
        // Special case for conditional SE/MB — surface SE + MB as two cols
        if (p.key === 'fines_quality_se_mb') {
          out.push({ label: 'SE', key: 'cf_se', align: 'right', get: r => {
              const v = r.custom_fields?.se;
              return v == null || v === '' ? '—' : String(v);
            } });
          out.push({ label: 'MB (g/kg)', key: 'cf_mb', align: 'right', get: r => {
              const v = r.custom_fields?.mb;
              return v == null || v === '' ? '—' : String(v);
            } });
          return;
        }
        const field = p.eval_field;
        const label = p.label_en && p.label_en.length < 32 ? p.label_en : (p.label.split('—')[1] || p.label).trim().slice(0, 28);
        out.push({
          label: `${label}${p.unit ? ` (${p.unit})` : ''}`,
          key: `cf_${field}`,
          align: 'right',
          get: (r) => {
            const v = r.custom_fields?.[field];
            if (v == null || v === '') return '—';
            return `${v}${p.unit ? ` ${p.unit}` : ''}`;
          },
        });
      });
      out.push(STATUS_COL);
      return out;
    }

    // ── Legacy fallback: concrete computed cols + admin custom fields ──
    if (famKey === 'concrete_mix') {
      out.push(
        { label: 'Class',       key: 'class',    get: r => r.mix_design?.class_of_concrete || '—' },
        { label: 'Cement Type', key: 'cemt',     get: r => r.mix_design?.cement_type || '—' },
        { label: 'Cement (kg/m³)', key: 'cem',   get: r => num(r.mix_design?.cement_content, 0), align: 'right' },
        { label: 'SCM %',       key: 'scm',      get: r => {
            const m = r.mix_design || {};
            const total = ['cement_content','fly_ash_content','silica_fume_content','ggbs_content'].reduce((a,k) => a + (+m[k]||0), 0);
            const scm = ['fly_ash_content','silica_fume_content','ggbs_content'].reduce((a,k) => a + (+m[k]||0), 0);
            return total > 0 ? ((scm/total)*100).toFixed(1) + '%' : '—';
          }, align: 'right' },
        { label: 'w/cm',        key: 'wcm',      get: r => {
            const m = r.mix_design || {};
            const total = ['cement_content','fly_ash_content','silica_fume_content','ggbs_content'].reduce((a,k) => a + (+m[k]||0), 0);
            return total > 0 ? ((+m.water_content||0)/total).toFixed(3) : '—';
          }, align: 'right' },
        { label: 'fc,28 decl.', key: 'fc28',     get: r => num(r.mix_design?.declared_fc28, 1), align: 'right' },
      );
    }
    customFields.forEach(cf => {
      out.push({
        label: cf.label || cf.name,
        key: `cf_${cf.name}`,
        get: (r) => {
          const v = r.custom_fields?.[cf.name];
          if (v == null || v === '') return '—';
          if (cf.type === 'boolean') return v ? 'Yes' : 'No';
          return `${v}${cf.unit ? ` ${cf.unit}` : ''}`;
        },
        align: (cf.type === 'number') ? 'right' : 'left',
      });
    });
    out.push(STATUS_COL);
    return out;
  }, [family.key, family.family, customFields, specRubricaCode, specTable]);

  const visible = useMemo(() => {
    if (statusFilter === 'approved')    return rows.filter(r => r.status === 'Approved');
    if (statusFilter === 'in_progress') return rows.filter(r => !['Approved','Rejected','Cancelled','Superseded'].includes(r.status));
    return rows;
  }, [rows, statusFilter]);

  const counts = useMemo(() => ({
    total:    rows.length,
    approved: rows.filter(r => r.status === 'Approved').length,
    active:   rows.filter(r => !['Approved','Rejected','Cancelled','Superseded'].includes(r.status)).length,
  }), [rows]);

  return (
    <div className="px-5 py-4" data-testid="ma-family-summary" style={{ color: t.text }}>
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <button onClick={onBack} className="p-1 rounded hover:bg-white/5" title="Back to Approval dashboard" data-testid="ma-family-back">
          <ArrowLeft className="w-4 h-4" style={{ color: t.textMuted }} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold tracking-wider" style={{ color: t.accent }}>
            MATERIALS APPROVAL · {(specRubricaCode || family.label).toString().toUpperCase()}
          </div>
          <h2 className="text-base font-bold" style={{ color: t.text }}>
            {specRubricaLabel || family.label}
          </h2>
          <p className="text-[11px]" style={{ color: t.textDim }}>
            {specTableKey && <><code>{specTableKey}</code> · </>}
            {projectId ? 'Submittals for this clause on the selected project.' : 'Submittals across all your projects.'}
          </p>
        </div>
        {specRubricaCode && projectId && (
          <AddSubmittalButton t={t} projectId={projectId} family={family} specTableKey={specTableKey} specRubricaCode={specRubricaCode} specRubricaLabel={specRubricaLabel} onCreated={(newRow) => setRows(prev => [newRow, ...prev])} />
        )}
      </div>

      {/* KPI + status filter */}
      <div className="flex items-center gap-2 flex-wrap mb-3 text-[11px]">
        <Chip t={t} label={`${counts.total} total`} />
        <Chip t={t} label={`${counts.approved} approved`} tone="approved" />
        <Chip t={t} label={`${counts.active} in progress`} tone="active" />
        <div className="ml-auto flex items-center gap-1">
          {[
            { k: 'all',         label: 'All' },
            { k: 'in_progress', label: 'In progress' },
            { k: 'approved',    label: 'Approved' },
          ].map(f => (
            <button key={f.k} onClick={() => setStatusFilter(f.k)}
              className="px-2 py-1 rounded text-[11px]"
              style={{
                background: statusFilter === f.k ? t.accentBg : t.inputBg,
                color: statusFilter === f.k ? t.accent : t.textMuted,
                border: `1px solid ${t.cardBorder}`,
              }}
              data-testid={`ma-family-filter-${f.k}`}
            >{f.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" style={{ color: t.accent }} /></div>
      ) : visible.length === 0 ? (
        <div className="border border-dashed rounded-lg py-10 text-center" style={{ borderColor: t.cardBorder }}>
          <Layers className="w-7 h-7 mx-auto mb-2" style={{ color: t.textDim }} />
          <div className="text-[12px] font-semibold mb-1" style={{ color: t.text }}>No {family.label.toLowerCase()} submittals {statusFilter === 'approved' ? 'approved' : 'yet'}</div>
          <div className="text-[11px]" style={{ color: t.textDim }}>
            Add submittals from the asset-level Materials Approval grid. Once they reach Approved, they'll be summarised here with the technical specs.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto border rounded" style={{ borderColor: t.cardBorder }}>
          <table className="text-[11px]" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
            <thead>
              <tr style={{ background: t.inputBg }}>
                {cols.map(col => (
                  <th key={col.key}
                    className="px-2 py-1.5 text-left font-semibold whitespace-nowrap"
                    style={{ color: t.textMuted, borderBottom: `1px solid ${t.cardBorder}`, textAlign: col.align || 'left' }}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map(row => (
                <tr key={row.id} className="hover:bg-white/[.02]" data-testid={`ma-family-row-${row.internal_ref}`}>
                  {cols.map(col => {
                    const content = col.get(row);
                    return (
                      <td key={col.key}
                        className="px-2 py-1 truncate"
                        style={{
                          borderBottom: `1px solid ${t.cardBorder}`,
                          textAlign: col.align || 'left',
                          maxWidth: 220,
                          cursor: col.clickable ? 'pointer' : 'default',
                        }}
                        onClick={() => col.clickable && setDrawerRow(row)}>
                        {col.strong ? <strong style={{ color: t.accent }} className="underline underline-offset-2 decoration-dotted">{content}</strong> : content}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drawerRow && <LazyDrawer t={t} row={drawerRow} onClose={() => setDrawerRow(null)} onPatched={(fresh) => { setRows(prev => prev.map(r => r.id === fresh.id ? fresh : r)); setDrawerRow(fresh); }} />}
    </div>
  );
}

function Chip({ t, label, tone }) {
  const palette = {
    approved: { bg: 'rgba(16,185,129,0.15)', fg: '#34d399' },
    active:   { bg: 'rgba(59,130,246,0.15)', fg: '#60a5fa' },
  };
  const c = palette[tone] || { bg: t.inputBg, fg: t.textMuted };
  return (
    <span className="px-2 py-0.5 rounded" style={{ background: c.bg, color: c.fg, border: `1px solid ${t.cardBorder}` }}>
      {label}
    </span>
  );
}

/* Lazy-import drawer to avoid circular imports */
function LazyDrawer(props) {
  const [Comp, setComp] = useState(null);
  useEffect(() => {
    import('./MaSubmittalDrawer').then(m => setComp(() => m.default));
  }, []);
  if (!Comp) return null;
  return <Comp {...props} submittal={props.row} />;
}

/* "+ Add row" button — creates a submittal prefilled with the selected
   spec rubrica so every new entry carries its clause cross-ref. */
function AddSubmittalButton({ t, projectId, family, specTableKey, specRubricaCode, specRubricaLabel, onCreated }) {
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const famKey = family.family || family.key;
      const row = await fetchSln('/api/prioris/ma/submittals', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          material_family: famKey,
          producer: '',
          product_name: '',
          status: 'Draft',
          spec_table_key: specTableKey,
          spec_rubrica_code: specRubricaCode,
          spec_rubrica_label: specRubricaLabel,
        }),
      });
      onCreated && onCreated(row);
    } catch (e) {
      alert(e.message || 'Failed to create submittal');
    }
    setBusy(false);
  };
  return (
    <button onClick={create} disabled={busy}
      className="px-2 py-1 rounded text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
      style={{ background: t.accent, color: '#000' }}
      data-testid="ma-add-submittal">
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
      Add submittal
    </button>
  );
}
